<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import DefaultProfile from '@/assets/svg/profile.svg'

export type ImageItem = string | null | undefined | { src: string | null | undefined, tooltip?: string }

const props = withDefaults(defineProps<{
  /** Array of images - can be strings or objects with src and optional tooltip */
  images: ImageItem[]
  /** Size of each avatar in pixels */
  size?: number
  /** Maximum number of avatars to display before showing the '+N' indicator */
  max?: number
  /** Controls the amount of overlap. Lower value means more overlap. */
  overlapSize?: number
  /** Border width around the masked area in pixels */
  borderWidth?: number
}>(), {
  size: 32,
  max: 3,
  overlapSize: 18,
  borderWidth: 2,
})

// Normalize images to a consistent format
const normalizedImages = computed(() => {
  return props.images.map((img) => {
    if (!img) return { src: null, tooltip: undefined }
    if (typeof img === 'string') return { src: img, tooltip: undefined }
    return { src: img.src || null, tooltip: img.tooltip }
  })
})

const imagesSliced = computed(() => {
  return normalizedImages.value.slice(0, props.max)
})

const plusCount = computed(() => {
  const remaining = normalizedImages.value.length - props.max
  return remaining > 0 ? remaining : 0
})

// CSS Variables for styling and animation control
const containerStyle = computed(() => ({
  '--size': `${props.size}px`,
  '--column': `${props.overlapSize}px`,
  '--border': `${props.borderWidth}px`,
}))

// Per-image error state is more robust
const imageErrors = ref<Record<string, boolean>>({})
const handleImageError = (imageUrl: string) => {
  imageErrors.value[imageUrl] = true
}
const hasError = (imageUrl: string) => !!imageErrors.value[imageUrl]

// Watch for changes in images prop to reset errors
watch(() => props.images, () => {
  imageErrors.value = {} // Reset errors when the image list changes
}, { deep: true })

// Track hovered index for z-index management
const hoveredIndex = ref<number | null>(null)
</script>

<template>
  <div
    class="multiple-image-container"
    :style="containerStyle"
  >
    <div class="items-wrapper">
      <div
        v-for="(image, index) in imagesSliced"
        :key="image.src || index"
        class="image-item"
        :class="{ 'first-item': index === 0 }"
        :style="{ zIndex: hoveredIndex === index ? 100 : 1 }"
        @mouseenter="hoveredIndex = index"
        @mouseleave="hoveredIndex = null"
      >
        <div class="avatar-holder">
          <span class="avatar">
            <img
              v-if="image.src && !hasError(image.src)"
              :src="image.src"
              alt=""
              loading="lazy"
              @error="() => handleImageError(image.src!)"
              v-tooltip.top="image.tooltip"
            >
            <!-- Fallback SVG -->
            <DefaultProfile v-else class="fallback-avatar" v-tooltip.top="image.tooltip" />
          </span>
        </div>
      </div>

      <!-- '+N' Indicator -->
      <div
        v-if="plusCount > 0"
        class="image-item plus-indicator-item"
        :style="{ zIndex: 0 }"
      >
        <div class="avatar-holder">
          <span class="avatar plus-indicator">
            +{{ plusCount }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Base container and grid setup */
.multiple-image-container {
  --circle: calc(((var(--border) * 2) + var(--size)) * 0.5);

  display: inline-flex;
  align-items: center;
  height: var(--size);
}

.items-wrapper {
  display: flex;
  height: var(--size);
  list-style-type: none;
  margin: 0;
  padding: 0;
}

.items-wrapper > * + * {
  margin-left: calc(var(--column) - var(--size));
}

/* Individual image item container */
.image-item {
  width: var(--size);
  height: var(--size);
  display: grid;
  align-content: end;
  position: relative;
}

/* Holder responsible for masking */
.avatar-holder {
  position: absolute;
  inset: 0;
  display: grid;
  align-content: end;
  overflow: hidden;
}

/* Apply mask to all items except the first one */
.image-item:not(.first-item) .avatar-holder {
  mask-image: radial-gradient(
    var(--circle) var(--circle) at
      calc(var(--circle) - var(--column) - var(--border))
      50%,
    transparent calc(var(--circle) - 0.5px),
    white var(--circle)
  );
  mask-size: 100% 100%;
  mask-position: 0 var(--size);
  transition: mask-position 0.15s ease-out;
}

/* Remove mask when item is hovered (z-index puts it on top) */
.image-item:hover .avatar-holder {
  mask-image: none;
}

/* The actual avatar circle */
.avatar {
  display: inline-block;
  width: var(--size);
  height: var(--size);
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  pointer-events: all;
  transition: translate 0.15s ease-out;
  z-index: 1;

  /* Light mode */
  background-color: var(--color-gray-100);
  border: var(--border) solid white;
}

:global(.dark) .avatar {
  background-color: var(--color-gray-700);
  border-color: #202427;
}

.avatar img,
.fallback-avatar {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.fallback-avatar {
  padding: 15%;
  box-sizing: border-box;

  /* Light mode */
  background-color: var(--color-gray-200);
  color: var(--color-gray-400);
}

:global(.dark) .fallback-avatar {
  background-color: var(--color-gray-600);
  color: var(--color-gray-400);
}

/* Hover Interactions */
.image-item:hover .avatar {
  translate: 0 -10%;
  z-index: 2;
}

.image-item:hover + .image-item .avatar-holder {
  mask-position: 0 calc(var(--size) * 0.9);
}

/* Style the '+N' indicator */
.plus-indicator-item {
  pointer-events: none;
}

.plus-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: calc(var(--size) * 0.38);
  font-weight: 600;
  text-align: center;
  translate: 0 0 !important;

  /* Light mode */
  background-color: var(--color-gray-100);
  color: var(--color-gray-500);
}

:global(.dark) .plus-indicator {
  background-color: var(--color-gray-700);
  color: var(--color-gray-400);
}
</style>
